// Hand-written mirror of the auto_domain_* schema as the Domain Candidates
// dashboards see it (fraud-dbs migration 20260909000000_tables_auto_domains.sql
// — auto_domain_candidate_flat / auto_domain_analysis / auto_domain_gram_yield
// / auto_domain_run, served by backend/queries/autoDomains.ts). No generated
// Supabase types exist in this repo; keep this in step with the view by hand.

import { parseDbTimestamp } from '@/lib/dates'
import type { DetailBadge } from '../EntryDetail/EntryDetailShell'

export type CandidateStatus = 'sourced' | 'filtered' | 'analyzing' | 'analyzed' | 'promoted' | 'discarded'

// The eight verdicts the heuristic classifier emits plus 'pending' — the
// dashboard's name for "no verdict yet" (NULL in the column; the summary RPC
// reports it under the same label).
export type CandidateVerdict =
  | 'pending' | 'dead' | 'unreachable' | 'blocked' | 'parked' | 'empty'
  | 'live_other' | 'brand_mention' | 'impersonation_other' | 'brand_lure'

export type BrandPosition = 'sld' | 'subdomain' | 'both' | 'none'

// What the latest probe found in front of the host (jupyter-fraud
// domain_candidate_analysis.observation_of_pass, 2026-10-24): Cloudflare
// challenging our lane and which of its pages we got, a block nobody could
// be named for, or a provider's own page. null = the latest probe saw no block.
export type BlockedReason =
  | 'cloudflare_challenge' | 'cloudflare_waf' | 'cloudflare_forbidden' | 'cloudflare_captcha'
  | 'challenge'
  | 'cloudflare_phishing_warning' | 'host_suspended' | 'registrar_suspended' | 'host_taken_down'
export const BLOCKED_REASON_LABEL: Record<string, string> = {
  cloudflare_challenge: 'CF challenge',
  cloudflare_waf: 'CF blocked',
  cloudflare_forbidden: 'CF 403',
  cloudflare_captcha: 'CF captcha',
  challenge: 'Block · unnamed',
  cloudflare_phishing_warning: 'CF phishing warning',
  host_suspended: 'Suspended by host',
  registrar_suspended: 'Suspended by registrar',
  host_taken_down: 'Taken down',
}
export const BLOCKED_REASON_TITLE: Record<string, string> = {
  cloudflare_challenge: 'Cloudflare interstitial ("Just a moment…", Turnstile): the page is behind a bot challenge our lane did not pass',
  cloudflare_waf: 'Cloudflare firewall refused our lane ("Sorry, you have been blocked")',
  cloudflare_forbidden: 'A bare 403 from Cloudflare with nothing of its own to show',
  cloudflare_captcha: 'A captcha served through Cloudflare',
  challenge: 'A 403 / challenge page naming no provider (nginx, Apache, a hosting panel)',
  cloudflare_phishing_warning: 'Cloudflare already flags this site as suspected phishing',
  host_suspended: "The hosting provider's suspension page",
  registrar_suspended: "The registrar's hold page",
  host_taken_down: 'The host says the site was taken down',
}
export const isCloudflareBlock = (r: string | null | undefined) => !!r && r.startsWith('cloudflare_') && r !== 'cloudflare_phishing_warning'
export const EVASIVE_TITLE = 'Evasive: Cloudflare challenging our lane on a domain registered within 30 days whose name carries the brand or one of its lure words — a kit hiding from inspection, not a dead host (decided 2026-09-30)'

// Where an analysis pass looked from: the 5G laptop with no proxy (ground
// truth -- nothing for a lure to detect; the only machine allowed to claim
// it), the Azure worker through Webshare (bulk tracking that sites do
// detect), or an unproxied dev machine / the e2e harness (dev_direct). 5G
// outranks the other two in the keep-best rule (libs/queries/auto_domains.py).
// What produced a verdict (jupyter-fraud domain_candidate_analysis.VERDICT_BASES).
export type VerdictBasis = 'e33_brand' | 'page_brand' | 'hostname_only' | 'none' | 'operator'
export const BASIS_LABEL: Record<VerdictBasis, string> = {
  e33_brand: 'e33 saw the brand',
  page_brand: 'brand on the page',
  hostname_only: 'brand in the name',
  none: 'no brand evidence',
  operator: 'set by hand',
}
export const BASIS_TITLE: Record<VerdictBasis, string> = {
  operator: 'An operator put the candidate in this bin by hand (Set verdict). It holds against every later automatic pass; only another hand-set verdict or a discard changes it',
  e33_brand: 'e33 saw the brand’s visual identity on the rendered page: the one evidence that makes a Brand impersonation, and it holds against every later pass',
  page_brand: 'The brand’s keyword appeared in the page text next to a sensitive input. Not an impersonation since 2026-10-17; yields to a pass in which e33 rendered the page',
  hostname_only: 'The brand token is in the hostname and nowhere else. Not an impersonation since 2026-10-13; yields to a pass in which e33 rendered the page',
  none: 'Nothing brand-related on the page or in the name',
}
export const LEGACY_BASIS_LABEL = 'legacy stamp'

export type Vantage = '5g' | 'azure_proxy' | 'dev_direct'
export const VANTAGE_LABEL: Record<Vantage, string> = { '5g': '5G', azure_proxy: 'Azure', dev_direct: 'Dev' }
export const VANTAGE_TONE: Record<Vantage, 'emerald' | 'sky' | 'neutral'> = { '5g': 'emerald', azure_proxy: 'sky', dev_direct: 'neutral' }
export const VANTAGE_TITLE: Record<Vantage, string> = {
  '5g': 'Analysed from the 5G laptop, no proxy — the ground truth',
  azure_proxy: 'Analysed from the Azure worker through a Webshare proxy — sites can detect it',
  dev_direct: 'Analysed unproxied from a dev machine or the e2e harness — not the 5G lane',
}

export type AutoDomainCandidateRow = {
  candidate_id: string
  run_id: string
  gram_id: string | null
  strategy_id: number | null
  platform_id: number
  platform_name_code: string | null
  platform_name: string | null
  run_start_kind: string
  run_started_at: string
  strategy_code: string | null
  strategy_name: string | null
  gram: string | null
  gram_kind: string | null
  term_freq: number | null
  doc_freq: number | null
  query_string: string | null
  query_type: string | null
  hostname: string
  registrable_domain: string | null
  tld: string | null
  domain_size: number | null
  label_count: number | null
  subject_common_name: string | null
  first_seen: string | null
  brand_position: BrandPosition
  term_freq_score: number | null
  size_score: number | null
  quality_score: number | null
  corroboration_count: number
  is_known: boolean
  domain_id: string | null
  status: CandidateStatus
  filtered_reason: string | null
  discarded_reason: string | null
  review_note: string | null
  raw_entry_id: string | null
  user_entry_id: string | null
  promoted_at: string | null
  promoted_by: string | null
  verdict: CandidateVerdict | null
  verdict_score: number | null
  is_alive: boolean | null
  analyzed_at: string | null
  // Best verdict vs latest probe (20260917000001). verdict/verdict_score are
  // the best any pass has seen, verdict_vantage which lane saw it; the
  // last_probe_* trio is what the most recent pass -- any lane -- saw. They
  // differ when a later probe got less (blocked, empty, parked...) than the
  // first contact, or looked from the lesser vantage.
  verdict_vantage: Vantage | null
  // What produced the verdict (20261017000000, BR-658): the strongest brand
  // evidence the stamping pass had. A Brand impersonation holds against
  // later passes only when e33 saw the brand (e33_brand); null is a stamp
  // from before the column, read as legacy until the replay fills it in.
  verdict_basis: VerdictBasis | null
  // a verdict set by hand (20261018000000): who and when; null = automatic
  verdict_set_by: string | null
  verdict_set_at: string | null
  // what the latest probe found in front of the host, and the sticky evasion
  // flag (20261024000000; see BlockedReason / EVASIVE_TITLE)
  blocked_reason: BlockedReason | null
  evasive: boolean
  last_probe_verdict: CandidateVerdict | null
  last_probe_vantage: Vantage | null
  last_probe_at: string | null
  found_by_gram_ids: string[]
  updated_at: string
  last_analysis_layer: string | null
  last_analysis_test_type: string | null
  last_analysis_is_alive: boolean | null
  last_analysis_verdict: string | null
  last_analysis_proxy_label: string | null
  last_analysis_at: string | null
  analysis_count: number | null
  // What the backoffice already holds on this hostname (see the view's own
  // comment): a known candidate is a re-sighting of a flagged domain.
  known_entry_count: number
  known_last_entry_at: string | null
  known_entry_ids: string[]
  // Registration (whois layer), denormalised: age is the strongest lure signal.
  whois_registered: boolean | null
  whois_registrar: string | null
  whois_created_at: string | null
  whois_expires_at: string | null
  whois_checked_at: string | null
  // the registrar's abuse desk and the hosting facts a takedown report is
  // addressed with (20260915000001); null until an analysis pass looked
  whois_abuse_email: string | null
  whois_abuse_phone: string | null
  whois_registrar_url: string | null
  whois_name_servers: string[] | null
  hosting_is_cloudflare: boolean | null
  resolved_ips: string[] | null
  // the hosting network behind the first IPv4 (IP RDAP, 20261026000000):
  // the provider's own abuse desk, next to the registrar's
  hosting_provider?: string | null
  hosting_abuse_email?: string | null
  hosting_abuse_domain?: string | null
  hosting_country?: string | null
  // One row per host since 2026-09-17: a later run that sees the host again
  // only moves last_seen_at. The four columns below are historical -- rows
  // written under the pre-2026-09-17 daily sighting model may still be
  // several per host (is_latest_sighting false on the superseded ones); new
  // rows are always sighting_count 1, latest, inherited_from null.
  previous_candidate_id: string | null
  first_sighted_at: string
  sighting_count: number
  is_latest_sighting: boolean
  inherited_from: 'discarded' | 'promoted' | 'analysis' | null
  /** The last sourcing run that still found this host in the certificate logs. */
  last_seen_at: string
  created_at: string
}

// How long a lure has been up: from the first sighting to the latest probe
// that still saw it live. Null when the last probe did not (or nothing probed).
export const LIVE_VERDICTS: ReadonlySet<string> = new Set(['brand_lure', 'impersonation_other', 'brand_mention', 'live_other'])
export function aliveForDays(c: Pick<AutoDomainCandidateRow, 'first_sighted_at' | 'last_probe_at' | 'last_probe_verdict' | 'verdict' | 'analyzed_at' | 'whois_created_at'>): number | null {
  const verdict = c.last_probe_verdict ?? c.verdict
  if (!verdict || !LIVE_VERDICTS.has(verdict)) return null
  const sighted = parseDbTimestamp(c.first_sighted_at)?.getTime()
  const to = parseDbTimestamp(c.last_probe_at ?? c.analyzed_at ?? undefined)?.getTime()
  if (sighted == null || to == null) return null
  // A domain cannot have been up under its current registration before it was
  // registered. A recycled / re-registered lure is routinely first sighted --
  // and can even carry an old CT certificate (first_seen) -- long before its
  // whois creation date, so anchoring purely on first sighting overstates
  // uptime and contradicts the "registered N d ago" column (e.g. mbway.shop:
  // first sighted 44 d ago, registered 12 d ago). Start the window at the
  // later of first sighting and registration.
  const whois = parseDbTimestamp(c.whois_created_at ?? undefined)?.getTime()
  const from = whois != null && whois > sighted ? whois : sighted
  if (to < from) return null
  return Math.floor((to - from) / 86_400_000)
}

export type SightingRow = Pick<AutoDomainCandidateRow,
  'candidate_id' | 'run_id' | 'run_started_at' | 'run_start_kind' | 'status' | 'verdict' | 'verdict_score' | 'is_alive' |
  'analyzed_at' | 'strategy_code' | 'gram' | 'inherited_from' | 'sighting_count' | 'is_latest_sighting' | 'created_at'>

// unified_entries_flat rows for a known candidate's existing entries,
// resolved by backend/queries/autoDomains.ts's detail query.
export type KnownEntryRow = {
  user_entry_id: string
  campaign_id: string | null
  campaign_type: string | null
  campaign_entry_count: number | null
  campaign_domain_count: number | null
  revision_status: string | null
  source: string | null
  source_type: string | null
  platform_id: number | null
  platform_name: string | null
  raw_entry_created_at: string | null
  last_created_at: string | null
  is_scam: boolean | null
  /** true for the entry this very candidate was promoted into */
  via_promotion?: boolean
}

// A candidate as seen from a campaign page ("auto-sourced sightings").
export type DomainSightingRow = Pick<AutoDomainCandidateRow,
  'candidate_id' | 'run_id' | 'platform_id' | 'platform_name_code' | 'hostname' | 'registrable_domain' | 'domain_id' |
  'strategy_code' | 'gram' | 'query_string' | 'status' | 'verdict' | 'verdict_score' | 'is_alive' | 'analyzed_at' |
  'quality_score' | 'brand_position' | 'corroboration_count' | 'promoted_at' | 'user_entry_id' | 'run_started_at' | 'created_at'>

export type AutoDomainAnalysisRow = {
  id: string
  candidate_id: string
  analysis_layer: 'network' | 'whois' | 'http' | 'browser' | 'content' | 'classify'
  test_type: string
  is_alive: boolean | null
  verdict: string | null
  score: number | null
  raw_result: Record<string, any> | null
  proxy_label: string | null
  proxy_country: string | null
  worker_id: string | null
  duration_ms: number | null
  error: string | null
  pass_id: string | null
  r2_key: string | null
  vantage: Vantage | null
  origin_job_run_id: string | null
  created_at: string
}

export type AutoDomainAnalysisPass = {
  passId: string
  createdAt: string
  proxyLabel: string | null
  /** The sighting this pass actually ran on; a re-sighting inherits passes from earlier ones. */
  candidateId: string
  runStartedAt: string | null
  inherited: boolean
  vantage: Vantage | null
  originJobRunId: string | null
  /** The job_run that produced the pass, when it was a job (a notebook pass has none). */
  originRun: {
    id: string; job_name: string; trigger: 'schedule' | 'manual' | 'chain'; requested_by: string | null; started_at: string | null; parent_run_id: string | null
    /** params.mobile_engine and the first of params.candidate_ids: which Verify button queued it (jobTypes verifyRunLabel) */
    mobile_engine?: string | null; first_candidate_id?: string | null
  } | null
  /** The oldest pass across every sighting of the host: the evidence of record. */
  isFirstContact: boolean
  rows: AutoDomainAnalysisRow[]
}

export type AutoDomainGramRow = {
  gram_id: string
  run_id: string
  strategy_code: string
  strategy_name: string
  start_kind: 'warm' | 'cold'
  gram: string
  gram_kind: string
  query_string: string
  query_type: string
  term_freq: number
  doc_freq: number
  seed_domain_size_avg: number | null
  coverage_ratio: number | null
  source_score: number | null
  provenance: Record<string, any> | null
  result_count: number
  candidate_count: number
  new_candidate_count: number
  had_hits: boolean
}

export type AutoDomainRun = {
  id: string
  platform_id: number
  start_kind: 'warm' | 'cold' | 'mixed'
  seed_domain_count: number
  /** 'schedule' | 'manual:<who>' | 'chain:<parent>' | 'notebook' — the job_run's own trigger (20260917000001) */
  triggered_by: string | null
  job_run_id: string | null
  started_at: string
  finished_at: string | null
  status: 'running' | 'completed' | 'failed'
  notes: string | null
  params: Record<string, any> | null
}

/** One pattern of a sourcing run with what its search brought back
 *  (GET /autodomains/runs/:id/queries): the gram row joined to its page-0
 *  auto_domain_query row. `executed` false = the run never reached the
 *  pattern (no query row at all). */
export type AutoDomainRunQueryRow = {
  gram_id: string
  strategy_code: string | null
  start_kind: 'warm' | 'cold' | null
  gram: string
  gram_kind: string
  query_string: string
  query_type: string
  source_score: number | null
  candidate_count: number
  new_candidate_count: number
  executed: boolean
  http_status: number | null
  total_count: number | null
  result_count: number
  error: string | null
  executed_at: string | null
}

/** answered = rows came back; zero = searched, nothing matched; not_fetched =
 *  a query row with an error (the browser session never handed this one
 *  back); not_run = no query row (the run died or trimmed it). */
export type RunQueryStatus = 'answered' | 'zero' | 'not_fetched' | 'not_run'

export const runQueryStatus = (r: AutoDomainRunQueryRow): RunQueryStatus =>
  !r.executed ? 'not_run' : r.error ? 'not_fetched' : r.result_count > 0 ? 'answered' : 'zero'

export const RUN_QUERY_STATUS_LABEL: Record<RunQueryStatus, string> = {
  answered: 'answered', zero: 'zero results', not_fetched: 'not fetched', not_run: 'not run',
}

export type AutoDomainCandidateDetail = {
  candidate: AutoDomainCandidateRow
  passes: AutoDomainAnalysisPass[]
  grams: AutoDomainGramRow[]
  run: AutoDomainRun | null
  knownEntries: KnownEntryRow[]
  sightings: SightingRow[]
  /** The promoted entry's pipeline steps (classification, evidence); empty before promotion. */
  pipelineSteps: Array<{ id: string; step: string; json_info: Record<string, unknown> | null; created_at: string }>
}

export type SummaryCell = { candidates: number; new_candidates: number; live: number }

export type AutoDomainSummary = {
  status: Record<string, SummaryCell>
  verdict: Record<string, SummaryCell>
  strategy: Record<string, SummaryCell>
  brand_position: Record<string, SummaryCell>
  tld: Record<string, SummaryCell>
  /** one key, 'true': the rows the evasion rule flagged (absent on a backend from before 2026-10-24) */
  evasive?: Record<string, SummaryCell>
  verdict_strategy: Array<{ verdict: string; strategy: string } & SummaryCell>
  total: number
  /** WHOIS creation month → candidates (brand lures vs the rest); gap months are present with 0. */
  registration_histogram: Array<{ month: string; count: number; lure: number; other: number }>
  registration_unregistered: number
  registration_unknown: number
  /** Sourcing quality in 0.05-wide bins (bin = lower edge). */
  quality_histogram: Array<{ bin: number; count: number }>
  quality_unscored: number
}

/** One screening pass (backend getAutoDomainScreenPasses): what the pass obtained,
 *  how many of those the screen looked at, how many it let stand. */
export type AutoDomainScreenPass = {
  run_id: string
  job_name: string
  /** job_name without the 'auto_domains.' prefix: source | screen | analyze | analyze_5g */
  job: string
  platform_id: number | null
  trigger: string | null
  started_at: string | null
  finished_at: string | null
  signals: number
  screened: number
  kept: number
  discarded: number
  infra: number
  unrelated: number
  /** sourcing passes only: rows the pass created (signals minus already tracked) */
  new_rows: number | null
  screened_pct: number | null
  kept_pct: number | null
  /** sourcing passes only: the auto_domain_run the pass created */
  source_run_id: string | null
  /** sourcing passes only: the pass's rows whose verdict is brand impersonation as of now; null where no run id links the rows */
  impersonation: number | null
}

export type CandidateListResponse = {
  candidates: AutoDomainCandidateRow[]
  hasMore: boolean
  total: number
  page: number
}

// ── Chips ─────────────────────────────────────────────────────────────────────

export const STATUS_ORDER: CandidateStatus[] = ['sourced', 'analyzing', 'analyzed', 'promoted', 'discarded', 'filtered']

export const STATUS_LABEL: Record<CandidateStatus, string> = {
  sourced: 'Pending',
  filtered: 'Filtered',
  analyzing: 'Analyzing',
  analyzed: 'Analyzed',
  promoted: 'Promoted',
  discarded: 'Discarded',
}

export const STATUS_TONE: Record<CandidateStatus, NonNullable<DetailBadge['tone']>> = {
  sourced: 'neutral',
  filtered: 'neutral',
  analyzing: 'sky',
  analyzed: 'emerald',
  promoted: 'emerald',
  discarded: 'red',
}

export const VERDICT_ORDER: CandidateVerdict[] = [
  'brand_lure', 'impersonation_other', 'brand_mention', 'live_other', 'parked', 'blocked', 'empty', 'unreachable', 'dead', 'pending',
]

export const VERDICT_LABEL: Record<CandidateVerdict, string> = {
  pending: 'No verdict',
  dead: 'Dead',
  unreachable: 'Unreachable',
  blocked: 'Blocked',
  parked: 'Parked',
  empty: 'Empty',
  live_other: 'Live · no lure',
  brand_mention: 'Brand mention',
  impersonation_other: 'Impersonates other',
  brand_lure: 'Brand impersonation',
}

export const VERDICT_TONE: Record<CandidateVerdict, NonNullable<DetailBadge['tone']>> = {
  pending: 'sky',
  dead: 'neutral',
  unreachable: 'neutral',
  blocked: 'neutral',
  parked: 'neutral',
  empty: 'neutral',
  live_other: 'sky',
  brand_mention: 'amber',
  impersonation_other: 'amber',
  brand_lure: 'red',
}

export const BRAND_POSITION_LABEL: Record<BrandPosition, string> = {
  sld: 'brand in SLD',
  subdomain: 'brand in subdomain',
  both: 'brand in both',
  none: 'no brand token',
}

// Same list-chip classes EntriesTable's STAGE_TONE uses, keyed by tone so a
// status/verdict renders the same colour in the list and in the detail's
// DetailBadge header.
export const CHIP_TONE_CLASS: Record<NonNullable<DetailBadge['tone']>, string> = {
  red: 'border-destructive/40 bg-danger-surface text-destructive',
  amber: 'border-warning-border bg-warning-surface text-warning',
  sky: 'border-signal/30 bg-signal-surface text-signal',
  emerald: 'border-success/30 bg-success-surface text-success',
  neutral: 'border-border bg-secondary text-muted-foreground',
}

export function verdictOf(row: { verdict: string | null }): CandidateVerdict {
  return (row.verdict as CandidateVerdict | null) ?? 'pending'
}
